import { useEffect, useMemo } from "react";
import { OperationVariables } from "@apollo/react-common";
import { useQuery } from "@apollo/react-hooks";
import { DocumentNode, OperationDefinitionNode } from "graphql";
import { v4 as uuidv4 } from "uuid";
import {
  addRefetchListener,
  clearRefetchQueries,
  removeRefetchListener,
} from "tasks/actions";
import { useStore } from "tasks/store";

export function useRefetchableQuery<
  TData = any,
  TVariables = OperationVariables,
>(query: DocumentNode, variables: OperationVariables) {
  const uuid = useMemo(() => uuidv4(), []);
  const { dispatch, state } = useStore();
  const { refetchListeners } = state;
  const currentListener = refetchListeners[uuid];
  const shouldRefetch = currentListener?.shouldRefetch;
  const {
    data,
    fetchMore,
    error,
    loading,
    networkStatus,
    startPolling,
    stopPolling,
    refetch,
  } = useQuery<TData, TVariables>(query, variables);
  const queryNames =
    (query.definitions || [])
      .filter(
        definition =>
          definition && (definition as OperationDefinitionNode)?.name,
      )
      .map(defintion => (defintion as OperationDefinitionNode).name?.value) ||
    [];
  useEffect(() => {
    if (queryNames && queryNames.length && uuid) {
      dispatch(addRefetchListener(queryNames, uuid));
    }
    return () => {
      if (queryNames && queryNames.length && uuid) {
        dispatch(removeRefetchListener(uuid));
      }
    };
  }, [...queryNames, uuid]);
  useEffect(() => {
    if (shouldRefetch) {
      refetch();
      dispatch(clearRefetchQueries(uuid));
    }
  }, [refetch, shouldRefetch, uuid]);
  return {
    data,
    fetchMore,
    error,
    loading,
    networkStatus,
    startPolling,
    stopPolling,
    refetch,
  };
}
